import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { List, Map } from 'immutable';
import PropTypes from 'prop-types';

import { getTotalStats } from 'src/utils/getTotalStats';
import { ACTION_DELETE } from 'src/components/helpers/DeleteItem';
import { mapApprovalsRowData } from 'src/utils/mapApprovalsRowData';
import { unwrap } from 'src/utils/ChangeSpy';
import Table from 'src/components/helpers/AgGridTable';

import { columnDef } from './ColomnDefs';
import { GridCard } from '../../../../helpers/GridCard';

import style from './style.module.css';

/**
 * Approval list component.
 *
 * @param props {Object}.
 * @param approvals {Immutable.List} Approval list.
 * @param common {Immutable.Map} Common object.
 * @param onChange {Function} Handle event of switching a row into edit mode.
 * @param onSort {Function} Handle event of sorting a column.
 * @param {boolean} canEditData Whether user can edit any active element that can be changed or clicked.
 */
const Approvals = ({ approvals, common, onSort, onChange, onRowInsert, onDelete, onRowDoubleClick, canEditData }) => {
  const gridApi = useRef(null);
  const cellClickToEdit = useRef(false);

  const checkEditable = useCallback(params => {
    if (params.node.data.id !== 'no_approval_lists') {
      return cellClickToEdit.current;
    }
  }, []);

  const preventEditing = useCallback(() => {
    cellClickToEdit.current = false;
  }, []);

  const handleInsert = useCallback(() => {
    if (!canEditData) return;
    if (!gridApi.current) return;

    const editingCells = gridApi.current.getEditingCells();

    if (editingCells && editingCells.length > 0) {
      return;
    }

    gridApi.current.stopEditing();

    const newRow = {
      id: 0,
      applistLabel: { value: '' },
      dateReceived: { value: null },
      dateSent: { value: null },
      numberApproved: 0,
      numberTargets: 0,
    };

    const count = gridApi.current.getModel().getRowCount();
    const transaction = gridApi.current.applyTransaction({
      add: [newRow],
      addIndex: count - 1,
    });

    const newIndex = transaction.add[0].rowIndex;

    cellClickToEdit.current = true;
    gridApi.current.startEditingCell({
      rowIndex: newIndex,
      colKey: 'applistLabel',
    });
  }, [canEditData]);

  const handleCellClicked = useCallback(
    event => {
      if (!canEditData) return;

      const {
        column: { colId },
        node: { rowIndex },
      } = event;

      // Don't turn to editing mode when grid is loading
      if (common.get('approvalLoading')) return;

      if (colId === 'applistLabel' || colId === 'dateReceived' || colId === 'dateSent') {
        cellClickToEdit.current = true;
        gridApi.current.startEditingCell({
          rowIndex,
          colKey: colId,
        });
      } else {
        gridApi.current.stopEditing();
      }
    },
    [canEditData, common],
  );

  const handleRowValueChanged = useCallback(
    ({ data }) => {
      if (data.id > 0 || data.mode === 'edit') {
        onChange({
          type: 'approvals',
          rowIndex: data.index,
          field: 'applistLabel',
          value: data.applistLabel.value,
        });
        onChange({
          type: 'approvals',
          rowIndex: data.index,
          field: 'dateReceived',
          value: data.dateReceived.value,
        });
        onChange({
          type: 'approvals',
          rowIndex: data.index,
          field: 'dateSent',
          value: data.dateSent.value,
        });
      } else if (data.id === 0 && (data.applistLabel.value || data.dateReceived.value || data.dateSent.value)) {
        onRowInsert({
          applistLabel: data.applistLabel.value,
          dateReceived: data.dateReceived.value,
          dateSent: data.dateSent.value,
        });
      }
    },
    [onChange, onRowInsert],
  );

  const handleContextMenuClick = useCallback(
    (event, data) => {
      if (!canEditData) return;

      const { id, numberTargets } = data.rowData;

      if (id !== 'no_approval_lists') {
        onDelete(event, id, numberTargets);
      }
    },
    [canEditData, onDelete],
  );

  const columns = useMemo(() => columnDef(checkEditable, handleInsert), [checkEditable, handleInsert]);

  useEffect(() => {
    if (gridApi.current) {
      gridApi.current.setPinnedBottomRowData(getTotalStats(approvals));
      gridApi.current.sizeColumnsToFit();
      gridApi.current.refreshCells({ force: true });
    }

    if (approvals.size && !unwrap(approvals.getIn([approvals.size - 1, 'id'])) && gridApi.current) {
      const rowIndex = approvals.size - 1;

      gridApi.current.startEditingCell({ rowIndex, colKey: 'applistLabel' });
      gridApi.current.setFocusedCell(rowIndex, 'applistLabel');
      gridApi.current.ensureIndexVisible(rowIndex);
    }
    cellClickToEdit.current = false;
  }, [approvals]);

  return (
    <div className={style.cell}>
      <GridCard className={classNames(style.grid, style.approvalGrid, 'full-height')}>
        <Table
          columnDefs={columns}
          contextActionList={ACTION_DELETE}
          editType="fullRow"
          getRowNodeId={data => data.index}
          isFetching={common.get('approvalLoading')}
          onCellClicked={handleCellClicked}
          onContextMenuClick={handleContextMenuClick}
          onGridReady={params => {
            gridApi.current = params.api;
            gridApi.current.addEventListener('rowEditingStarted', preventEditing);
          }}
          onRowDoubleClicked={params => {
            if (!canEditData) return;

            if (params.data.id > 0) {
              onRowDoubleClick({
                row: Map({
                  id: params.data.id,
                  projectId: params.data.projectId,
                }),
                type: 'approvals',
              });
            } else if (params.data.id === 'no_approval_lists') {
              onRowDoubleClick({
                row: Map({
                  id: 'no_approval_lists',
                  projectId: params.data.projectId,
                }),
                type: 'approvals',
              });
            }
          }}
          onRowValueChanged={handleRowValueChanged}
          onSortModelChanged={sortModel => {
            if (!canEditData) return;

            if (gridApi.current) {
              gridApi.current.stopEditing(true);
            }

            if (sortModel && sortModel.length) {
              onSort(sortModel, {
                type: 'approvals',
                direction: sortModel[0].sort === 'asc' ? 'down' : 'up',
                field: sortModel[0].colId,
              });
            }
          }}
          rowData={mapApprovalsRowData(approvals)}
          sortable
        />
      </GridCard>
    </div>
  );
};

Approvals.propTypes = {
  approvals: PropTypes.instanceOf(List).isRequired,
  canEditData: PropTypes.bool.isRequired,
  common: PropTypes.instanceOf(Map).isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRowDoubleClick: PropTypes.func.isRequired,
  onRowInsert: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
};

export default Approvals;
